/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import SideNavEyeLogoIcon from '@ion/dls/src/icons/current/SideNavEyeLogoIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import IOnSuiteLogo from '../../components/logo/IOnSuiteLogo';

/**
 * Defines the props for the current navigation brand block.
 */
interface NavigationHeaderBrandProps {
  collapsed: boolean;
  onBrandClick: () => void;
}

const useStyles = makeStyles(() => ({
  brandRow : {
    alignItems     : 'center',
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'flex-start',
    minHeight      : 44,
    padding        : '0 0 8px 2px'
  },
  // Figma: the logo's 44px row, then the 44px expand button row below it,
  // then 8px before the search and tracking card ...
  brandRowCollapsed : {
    flexDirection  : 'column',
    justifyContent : 'center',
    minHeight      : 44,
    padding        : '0 0 52px'
  },
  brandInner : {
    alignItems        : 'center',
    background        : 'transparent',
    border            : 0,
    cursor            : 'pointer',
    display           : 'flex',
    gap               : 2,
    minWidth          : 0,
    padding           : 0,
    '&:focus-visible' : {
      borderRadius  : 8,
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      outlineOffset : 4
    }
  }
}));

/**
 * Renders the current side navigation brand row.
 *
 * @param props Defines the collapsed state and click behavior for brand layout.
 * @returns The current side navigation brand row.
 */
const NavigationHeaderBrand = ({collapsed, onBrandClick}: NavigationHeaderBrandProps) => {
  const classes = useStyles();

  return (
    <div className={classnames(classes.brandRow, {
      [classes.brandRowCollapsed] : collapsed
    })}>
      <button type='button'
        className={classes.brandInner}
        aria-label='Go to app entry screen'
        onClick={onBrandClick}>
        <SideNavEyeLogoIcon iconSize={48} />
        <Render.If condition={!collapsed}>
          <IOnSuiteLogo width={40}
            height={21}
            fill={dlsColor('aaBlue')} />
        </Render.If>
      </button>
    </div>
  );
};

export default memo(NavigationHeaderBrand);
